import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { ArrowLeft, Lock, Loader2, DollarSign, Landmark, Briefcase, TrendingUp, Shield } from 'lucide-react';
import { useYear } from '../contexts/YearContext';
import { getRoleDisplayName } from '../utils/roleMapping';
import Dashboard from './Dashboard';
import WeeklyTracker from './WeeklyTracker';
import MonthlyChecks from './MonthlyChecks';
import ExpenseTracker from './ExpenseTracker';
import Reports from './Reports';
import BusinessOverview from './BusinessOverview';
import AccountBalances from './AccountBalances';

function TeamMemberView() {
  const { memberId } = useParams();
  const navigate = useNavigate();
  const [memberInfo, setMemberInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');
  const { selectedYear } = useYear();

  useEffect(() => {
    loadMemberInfo();
  }, [memberId, selectedYear]);

  const loadMemberInfo = async () => {
    try {
      setLoading(true);
      // Get member's basic info from team members list
      const teamResponse = await axios.get(`${API}/team/members?year=${selectedYear}`);
      const member = teamResponse.data.find(m => m.id === memberId);
      
      if (!member) {
        setError('Team member not found or you do not have permission to view this member');
      } else {
        setMemberInfo(member);
      }
      setError(null);
    } catch (err) {
      console.error('Error loading member info:', err);
      setError('Failed to load team member information');
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'dashboard', label: 'Dashboard' },
    { id: 'weekly', label: '52-Week Tracker' },
    { id: 'monthly', label: 'Monthly Checks' },
    { id: 'expenses', label: 'Expenses' },
    { id: 'accounts', label: 'Account Balances' },
    { id: 'reports', label: 'Reports' },
    { id: 'business', label: 'Business Overview' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error || !memberInfo) {
    return (
      <div className="space-y-4">
        <button
          onClick={() => navigate('/team')}
          className="flex items-center space-x-2 text-blue-600 hover:text-blue-700"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Back to Team</span>
        </button>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <p className="text-red-800">{error || 'Member not found'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Read-Only Banner */}
      <div className="bg-yellow-50 dark:bg-yellow-900/30 border-l-4 border-yellow-400 dark:border-yellow-600 p-4 rounded-lg">
        <div className="flex items-center">
          <Lock className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mr-3" />
          <div>
            <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
              Viewing: {memberInfo.name}&apos;s Dashboard (Read-Only)
            </p>
            <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-1">
              You&apos;re viewing this team member&apos;s data in read-only mode. You cannot make changes to their account.
            </p>
          </div>
        </div>
      </div>

      {/* Back Button and Member Info */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        <button
          onClick={() => navigate('/team')}
          className="flex items-center space-x-2 text-blue-600 hover:text-blue-700 mb-4"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Back to Team</span>
        </button>
        
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white dark:text-white">{memberInfo.name}</h1>
            <p className="text-gray-900 dark:text-gray-100 font-semibold">{memberInfo.email}</p>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-900 dark:text-gray-100 font-bold">Role</p>
            <p className="text-lg font-semibold text-gray-900 dark:text-white">{getRoleDisplayName(memberInfo.role)}</p>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md">
        <div className="border-b border-gray-200 dark:border-gray-700 dark:border-gray-700">
          <nav className="flex overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-6 py-4 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:text-white hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'dashboard' && <TeamMemberDashboard memberId={memberId} />}
          {activeTab === 'weekly' && <TeamMemberWeekly memberId={memberId} />}
          {activeTab === 'monthly' && <TeamMemberMonthly memberId={memberId} />}
          {activeTab === 'expenses' && <TeamMemberExpenses memberId={memberId} />}
          {activeTab === 'accounts' && <TeamMemberAccountBalances memberId={memberId} />}
          {activeTab === 'reports' && <TeamMemberReports memberId={memberId} />}
          {activeTab === 'business' && <TeamMemberBusinessOverview memberId={memberId} />}
        </div>
      </div>
    </div>
  );
}

// Sub-components for each tab
function TeamMemberDashboard({ memberId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { selectedYear } = useYear();

  useEffect(() => {
    loadData();
  }, [memberId, selectedYear]);

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/team/member/${memberId}/dashboard?year=${selectedYear}`);
      setData(response.data);
    } catch (err) {
      console.error('Error loading dashboard:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>;
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
        <p className="text-sm font-bold text-gray-900 dark:text-gray-100 mb-1">Total Income</p>
        <p className="text-2xl font-bold text-blue-700 dark:text-blue-400">${data?.total_income?.toLocaleString() || '0.00'}</p>
      </div>
      <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
        <p className="text-sm font-bold text-gray-900 dark:text-gray-100 mb-1">Weeks Logged</p>
        <p className="text-2xl font-bold text-green-700 dark:text-green-400">{data?.weeks_logged || 0}</p>
      </div>
      <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4">
        <p className="text-sm font-bold text-gray-900 dark:text-gray-100 mb-1">Monthly Checks</p>
        <p className="text-2xl font-bold text-purple-700 dark:text-purple-400">{data?.monthly_checks_count || 0}</p>
      </div>
    </div>
  );
}

function TeamMemberWeekly({ memberId }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const { selectedYear } = useYear();

  useEffect(() => {
    loadData();
  }, [memberId, selectedYear]);

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/team/member/${memberId}/weekly-income?year=${selectedYear}`);
      setEntries(response.data);
    } catch (err) {
      console.error('Error loading weekly income:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>;
  }

  if (entries.length === 0) {
    return <div className="text-center py-8 text-gray-900 dark:text-gray-100 font-bold">No weekly income entries for {selectedYear}</div>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-gray-50 dark:bg-gray-700">
          <tr>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-900 dark:text-gray-100 font-bold">Week</th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-900 dark:text-gray-100 font-bold">Pay Date</th>
            <th className="px-4 py-2 text-right text-xs font-medium text-gray-900 dark:text-gray-100 font-bold">Income</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 dark:divide-gray-700 dark:divide-gray-700">
          {entries.map((entry, idx) => (
            <tr key={entry.id || `entry-${idx}`}>
              <td className="px-4 py-2 text-sm font-medium text-gray-900 dark:text-white dark:text-white">
                {entry.week_number || 'N/A'}
              </td>
              <td className="px-4 py-2 text-sm text-gray-900 dark:text-gray-100 font-semibold">
                {entry.pay_date ? new Date(entry.pay_date).toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: '2-digit', 
                  day: '2-digit' 
                }) : 'N/A'}
              </td>
              <td className="px-4 py-2 text-sm text-right font-semibold text-green-600">
                ${entry.income?.toLocaleString() || '0.00'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TeamMemberMonthly({ memberId }) {
  const [checks, setChecks] = useState([]);
  const [loading, setLoading] = useState(true);
  const { selectedYear } = useYear();

  useEffect(() => {
    loadData();
  }, [memberId, selectedYear]);

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/team/member/${memberId}/monthly-checks?year=${selectedYear}`);
      setChecks(response.data);
    } catch (err) {
      console.error('Error loading monthly checks:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>;
  }

  if (checks.length === 0) {
    return <div className="text-center py-8 text-gray-900 dark:text-gray-100 font-bold">No monthly checks for {selectedYear}</div>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-gray-50 dark:bg-gray-700">
          <tr>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-900 dark:text-gray-100 font-bold">Date</th>
            <th className="px-4 py-2 text-right text-xs font-medium text-gray-900 dark:text-gray-100 font-bold">Amount</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 dark:divide-gray-700 dark:divide-gray-700">
          {checks.map((check, idx) => (
            <tr key={check.id || `check-${idx}`}>
              <td className="px-4 py-2 text-sm font-medium text-gray-900 dark:text-white dark:text-white">
                {check.date ? new Date(check.date).toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: '2-digit', 
                  day: '2-digit' 
                }) : 'N/A'}
              </td>
              <td className="px-4 py-2 text-sm text-right font-semibold text-green-600">
                ${check.amount?.toLocaleString() || '0.00'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TeamMemberExpenses({ memberId }) {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const { selectedYear } = useYear();

  useEffect(() => {
    loadData();
  }, [memberId, selectedYear]);

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/team/member/${memberId}/expenses?year=${selectedYear}`);
      setExpenses(response.data);
    } catch (err) {
      console.error('Error loading expenses:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>;
  }

  if (expenses.length === 0) {
    return <div className="text-center py-8 text-gray-900 dark:text-gray-100 font-bold">No expenses for {selectedYear}</div>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-gray-50 dark:bg-gray-700">
          <tr>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-900 dark:text-gray-100 font-bold">Date</th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-900 dark:text-gray-100 font-bold">Description</th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-900 dark:text-gray-100 font-bold">Account</th>
            <th className="px-4 py-2 text-right text-xs font-medium text-gray-900 dark:text-gray-100 font-bold">Amount</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 dark:divide-gray-700 dark:divide-gray-700">
          {expenses.map((expense, idx) => (
            <tr key={expense.id || `expense-${idx}`}>
              <td className="px-4 py-2 text-sm font-medium text-gray-900 dark:text-white dark:text-white">
                {expense.date ? new Date(expense.date).toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: '2-digit', 
                  day: '2-digit' 
                }) : 'N/A'}
              </td>
              <td className="px-4 py-2 text-sm text-gray-900 dark:text-gray-100 font-semibold">
                {expense.description || 'No description'}
              </td>
              <td className="px-4 py-2 text-sm capitalize text-gray-900 dark:text-gray-100 font-semibold">
                {expense.account?.replace('_', ' ') || 'N/A'}
              </td>
              <td className="px-4 py-2 text-sm text-right font-semibold text-red-600">
                ${expense.amount?.toLocaleString() || '0.00'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Team Member Account Balances Component
function TeamMemberAccountBalances({ memberId }) {
  const [balances, setBalances] = useState(null);
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [memberId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const [balancesRes, planRes] = await Promise.all([
        axios.get(`${API}/team/member/${memberId}/account-balances`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/financial-plan`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setBalances(balancesRes.data);
      setPlan(planRes.data);
    } catch (err) {
      console.error('Error loading account balances:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const totalBalance = balances
    ? balances.operations + balances.taxes + balances.business_growth + (balances.savings || 0) + (balances.buffer || 0) +
      (balances.custom_allocations ? Object.values(balances.custom_allocations).reduce((sum, val) => sum + val, 0) : 0)
    : 0;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount);
  };

  return (
    <div className="space-y-6">
      {/* Total Balance */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white rounded-lg shadow-lg p-8">
        <div className="flex items-center space-x-3 mb-2">
          <DollarSign className="w-8 h-8" />
          <h2 className="text-xl font-semibold">Total Across All Accounts</h2>
        </div>
        <p className="text-5xl font-bold">
          {formatCurrency(totalBalance)}
        </p>
      </div>

      {/* Individual Accounts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Personal Account */}
        <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-6 border border-green-200 dark:border-green-700">
          <div className="flex items-center space-x-3 mb-3">
            <DollarSign className="w-6 h-6 text-green-600 dark:text-green-400" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Personal Account</h3>
          </div>
          <p className="text-3xl font-bold text-green-700 dark:text-green-400 mb-2">
            {formatCurrency(balances?.operations || 0)}
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-300">Living expenses & bills ({plan?.allocation?.personal || 55}%)</p>
        </div>

        {/* Business Account */}
        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-6 border border-blue-200 dark:border-blue-700">
          <div className="flex items-center space-x-3 mb-3">
            <TrendingUp className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Business Account</h3>
          </div>
          <p className="text-3xl font-bold text-blue-700 dark:text-blue-400 mb-2">
            {formatCurrency(balances?.business_growth || 0)}
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-300">Marketing, leads & business costs ({plan?.allocation?.business || 20}%)</p>
        </div>

        {/* Taxes Account */}
        <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-6 border border-red-200 dark:border-red-700">
          <div className="flex items-center space-x-3 mb-3">
            <Briefcase className="w-6 h-6 text-red-600 dark:text-red-400" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Taxes Account</h3>
          </div>
          <p className="text-3xl font-bold text-red-700 dark:text-red-400 mb-2">
            {formatCurrency(balances?.taxes || 0)}
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-300">Tax reserve ({plan?.allocation?.taxes || 20}%)</p>
        </div>

        {/* Savings Account */}
        <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-6 border border-yellow-200 dark:border-yellow-700">
          <div className="flex items-center space-x-3 mb-3">
            <Shield className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Savings Account</h3>
          </div>
          <p className="text-3xl font-bold text-yellow-700 dark:text-yellow-400 mb-2">
            {formatCurrency(balances?.savings || 0)}
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-300">Emergency fund & future goals ({plan?.allocation?.savings || 5}%)</p>
        </div>

        {/* Buffer Account — first-class bucket since Feb 2026 */}
        <div className="bg-cyan-50 dark:bg-cyan-900/20 rounded-lg p-6 border border-cyan-200 dark:border-cyan-700">
          <div className="flex items-center space-x-3 mb-3">
            <Landmark className="w-6 h-6 text-cyan-600 dark:text-cyan-400" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Buffer Account</h3>
          </div>
          <p className="text-3xl font-bold text-cyan-700 dark:text-cyan-400 mb-2">
            {formatCurrency(balances?.buffer || 0)}
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-300">Unallocated capital retained outside plan buckets</p>
        </div>
      </div>
    </div>
  );
}

// Team Member Reports Component
function TeamMemberReports({ memberId }) {
  const [monthlyBreakdown, setMonthlyBreakdown] = useState([]);
  const [loading, setLoading] = useState(true);
  const { selectedYear } = useYear();

  useEffect(() => {
    if (selectedYear && memberId) {
      loadData();
    }
  }, [selectedYear, memberId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/team/member/${memberId}/reports?year=${selectedYear}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMonthlyBreakdown(response.data);
    } catch (err) {
      console.error('Error loading report data:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Monthly Income Comparison ({selectedYear})</h2>
          <p className="text-gray-600 dark:text-gray-300 mt-1">Track gross income and net profit month by month</p>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-800 text-white">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold">Month</th>
                <th className="px-4 py-3 text-right text-sm font-semibold">Gross Income</th>
                <th className="px-4 py-3 text-right text-sm font-semibold">Business Expenses</th>
                <th className="px-4 py-3 text-right text-sm font-semibold bg-green-700">Net After Business</th>
                <th className="px-4 py-3 text-right text-sm font-semibold">Personal Expenses</th>
                <th className="px-4 py-3 text-right text-sm font-semibold bg-blue-700">Net After All</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {monthlyBreakdown.map((month, idx) => (
                <tr 
                  key={month.month || `month-${idx}`}
                  className={`hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                    idx % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-700'
                  }`}
                >
                  <td className="px-4 py-3 font-bold text-gray-900 dark:text-white">{month.month}</td>
                  <td className="px-4 py-3 text-right text-gray-900 dark:text-gray-100 font-semibold">
                    {formatCurrency(month.gross_income)}
                  </td>
                  <td className="px-4 py-3 text-right text-red-700 dark:text-red-400 font-semibold">
                    {formatCurrency(month.business_expenses)}
                  </td>
                  <td className="px-4 py-3 text-right text-green-800 dark:text-green-400 font-bold bg-green-50 dark:bg-green-900/20">
                    {formatCurrency(month.net_after_business)}
                  </td>
                  <td className="px-4 py-3 text-right text-orange-700 dark:text-orange-400 font-semibold">
                    {formatCurrency(month.personal_expenses)}
                  </td>
                  <td className="px-4 py-3 text-right text-blue-800 dark:text-blue-400 font-bold bg-blue-50 dark:bg-blue-900/20">
                    {formatCurrency(month.net_after_all)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
        <h3 className="font-semibold text-blue-900 dark:text-blue-200 mb-2">📊 Report Explanation:</h3>
        <ul className="text-sm text-blue-800 dark:text-blue-300 space-y-1">
          <li><strong>Gross Income:</strong> Total income from all sources (weekly + monthly)</li>
          <li><strong>Net After Business:</strong> Income remaining after business expenses</li>
          <li><strong>Net After All:</strong> Final amount after all business and personal expenses</li>
        </ul>
      </div>
    </div>
  );
}

// Team Member Business Overview Component
function TeamMemberBusinessOverview({ memberId }) {
  // We'll render the BusinessOverview component but configure it to show the member's data
  // by temporarily setting up a context override
  return (
    <div>
      <TeamMemberBusinessOverviewContent memberId={memberId} />
    </div>
  );
}

function TeamMemberBusinessOverviewContent({ memberId }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const { selectedYear } = useYear();
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [weeklyBreakdown, setWeeklyBreakdown] = useState(null);
  const [loadingWeekly, setLoadingWeekly] = useState(false);

  useEffect(() => {
    loadData();
  }, [memberId, selectedYear]);

  useEffect(() => {
    if (selectedYear && selectedMonth) {
      loadWeeklyBreakdown();
    }
  }, [selectedYear, selectedMonth, memberId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Get member's business overview data
      const response = await axios.get(`${API}/reports/member-business-overview/${memberId}?year=${selectedYear}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(response.data);
    } catch (error) {
      console.error('Failed to load member business overview:', error);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const loadWeeklyBreakdown = async () => {
    try {
      setLoadingWeekly(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/reports/member-weekly-expense-breakdown/${memberId}?year=${selectedYear}&month=${selectedMonth}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setWeeklyBreakdown(response.data);
    } catch (error) {
      console.error('Failed to load weekly expense breakdown:', error);
      setWeeklyBreakdown(null);
    } finally {
      setLoadingWeekly(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount);
  };

  // Calculate totals
  const totalAdvance = data.reduce((sum, month) => sum + (month.advance || 0), 0);
  const totalMonthly = data.reduce((sum, month) => sum + (month.monthly || 0), 0);
  const totalGross = data.reduce((sum, month) => sum + (month.gross || 0), 0);
  const totalExpenses = data.reduce((sum, month) => sum + (month.expenses || 0), 0);
  const totalNetProfit = data.reduce((sum, month) => sum + (month.net_profit || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Business Overview Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-800 text-white">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold">Month</th>
                <th className="px-4 py-3 text-right text-sm font-semibold">Advance</th>
                <th className="px-4 py-3 text-right text-sm font-semibold">Monthly</th>
                <th className="px-4 py-3 text-right text-sm font-semibold bg-green-700">Gross</th>
                <th className="px-4 py-3 text-right text-sm font-semibold">Expenses</th>
                <th className="px-4 py-3 text-right text-sm font-semibold bg-blue-700">Net Profit</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700 dark:divide-gray-700">
              {data.map((month, index) => (
                <tr 
                  key={month.month || `bomonth-${index}`} 
                  className={`hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${index % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-700'}`}
                >
                  <td className="px-4 py-3 font-bold text-gray-900 dark:text-white">{month.month}</td>
                  <td className="px-4 py-3 text-right text-blue-700 dark:text-blue-400 font-semibold">
                    {formatCurrency(month.advance)}
                  </td>
                  <td className="px-4 py-3 text-right text-purple-700 dark:text-purple-400 font-semibold">
                    {formatCurrency(month.monthly)}
                  </td>
                  <td className="px-4 py-3 text-right text-green-800 dark:text-green-400 font-bold bg-green-50 dark:bg-green-900/20">
                    {formatCurrency(month.gross)}
                  </td>
                  <td className="px-4 py-3 text-right text-red-700 dark:text-red-400 font-semibold">
                    {formatCurrency(month.expenses)}
                  </td>
                  <td className="px-4 py-3 text-right text-blue-800 dark:text-blue-400 font-bold bg-blue-50 dark:bg-blue-900/20">
                    {formatCurrency(month.net_profit)}
                  </td>
                </tr>
              ))}
              
              {/* Total Row */}
              <tr className="bg-gray-900 text-white font-bold">
                <td className="px-4 py-4 text-left">TOTAL</td>
                <td className="px-4 py-4 text-right">{formatCurrency(totalAdvance)}</td>
                <td className="px-4 py-4 text-right">{formatCurrency(totalMonthly)}</td>
                <td className="px-4 py-4 text-right bg-green-800">{formatCurrency(totalGross)}</td>
                <td className="px-4 py-4 text-right">{formatCurrency(totalExpenses)}</td>
                <td className="px-4 py-4 text-right bg-blue-800">{formatCurrency(totalNetProfit)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Legend */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-semibold text-blue-900 mb-2">📊 Overview Explanation:</h3>
        <ul className="text-sm text-blue-800 space-y-1">
          <li><strong>Advance:</strong> Business portion of weekly income (Business + Savings allocation)</li>
          <li><strong>Monthly:</strong> Business portion of monthly checks</li>
          <li><strong>Gross:</strong> Total business income (Advance + Monthly)</li>
          <li><strong>Expenses:</strong> Business expenses from Business Expense account only</li>
          <li><strong>Net Profit:</strong> Actual business profit (Gross - Expenses)</li>
        </ul>
      </div>

      {/* Weekly Expense Breakdown */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white dark:text-white">Weekly Expense Breakdown by Category</h2>
          <div className="w-64">
            <div className="bg-purple-100 dark:bg-purple-900/30 px-3 py-1 rounded-t-lg mb-1">
              <label className="text-base font-extrabold text-purple-900 dark:text-purple-300">SELECT MONTH</label>
            </div>
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
              className="w-full px-4 py-2 border-2 border-purple-300 dark:border-purple-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-medium"
            >
              <option value={1}>January</option>
              <option value={2}>February</option>
              <option value={3}>March</option>
              <option value={4}>April</option>
              <option value={5}>May</option>
              <option value={6}>June</option>
              <option value={7}>July</option>
              <option value={8}>August</option>
              <option value={9}>September</option>
              <option value={10}>October</option>
              <option value={11}>November</option>
              <option value={12}>December</option>
            </select>
          </div>
        </div>

        {loadingWeekly ? (
          <div className="flex items-center justify-center h-32">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : weeklyBreakdown ? (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-800 text-white">
                  <th className="border border-gray-600 px-3 py-2 text-left text-sm font-semibold">Category</th>
                  {weeklyBreakdown.weeks.map((week, idx) => (
                    <th key={`wk-${week}-${idx}`} className="border border-gray-600 px-3 py-2 text-center text-sm font-semibold whitespace-nowrap">
                      {week}
                    </th>
                  ))}
                  <th className="border border-gray-600 px-3 py-2 text-center text-sm font-semibold bg-blue-700">
                    Monthly Total
                  </th>
                </tr>
              </thead>
              <tbody>
                {weeklyBreakdown.categories.map((category, catIdx) => (
                  <tr key={category.category || `cat-${catIdx}`} className={catIdx % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-700'}>
                    <td className="border border-gray-300 dark:border-gray-600 px-3 py-2 font-medium text-gray-900 dark:text-white dark:text-white">
                      {category.category}
                    </td>
                    {category.weeks.map((amount, weekIdx) => (
                      <td key={`${category.category}-w${weekIdx}`} className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-center text-gray-900 dark:text-gray-100 font-semibold">
                        {amount > 0 ? formatCurrency(amount) : '-'}
                      </td>
                    ))}
                    <td className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-center font-semibold text-blue-700 bg-blue-50 dark:bg-blue-900/20 dark:bg-blue-900/20">
                      {category.monthly_total > 0 ? formatCurrency(category.monthly_total) : '-'}
                    </td>
                  </tr>
                ))}

                {/* Grand Totals Row */}
                <tr className="bg-yellow-100 dark:bg-yellow-900/30 font-bold">
                  <td className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-gray-900 dark:text-white font-bold">Grand Totals</td>
                  {weeklyBreakdown.week_totals.map((total, idx) => (
                    <td key={`total-${weeklyBreakdown.weeks?.[idx] || idx}`} className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-center text-gray-900 dark:text-white font-bold">
                      {formatCurrency(total)}
                    </td>
                  ))}
                  <td className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-center text-blue-900 dark:text-blue-200 bg-blue-100 dark:bg-blue-900/30 font-bold">
                    {formatCurrency(weeklyBreakdown.grand_total)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center text-gray-900 dark:text-gray-100 font-semibold py-8">
            Select a month to view weekly expense breakdown
          </div>
        )}
      </div>
    </div>
  );
}


export default TeamMemberView;
